import api, { fetcher } from '@/lib/api';
import { LoginRequest, RegisterRequest, AuthResponse, User, ViaCepResponse, UpdateProfileRequest } from '@/types/auth';
import axios from 'axios';
import Cookies from 'js-cookie';

export class AuthService {
  static async login(data: LoginRequest): Promise<AuthResponse> {
    try {
      const response = await fetcher<AuthResponse>({ method: 'post', url: '/api/auth/login', data });
      
      if (response.success && response.token) {
        // Salvar token e dados do usuário
        Cookies.set('auth_token', response.token, { expires: 7 });
        Cookies.set('user_data', JSON.stringify(response.user), { expires: 7 });
      }
      
      return response;
    } catch (error) {
      const message = axios.isAxiosError(error)
        ? error.response?.data?.message ?? 'Erro ao fazer login'
        : 'Erro ao fazer login';
      throw new Error(message);
    }
  }

  static async register(data: RegisterRequest): Promise<AuthResponse> {
    try {
      return await fetcher<AuthResponse>({ method: 'post', url: '/api/auth/register', data });
    } catch (error) {
      const message = axios.isAxiosError(error)
        ? error.response?.data?.message ?? 'Erro ao registrar usuário'
        : 'Erro ao registrar usuário';
      throw new Error(message);
    }
  }

  static async getProfile(): Promise<User> {
    try {
      const response = await fetcher<AuthResponse>({ method: 'get', url: '/api/auth/profile' });
      if (!response.user) {
        throw new Error('Erro ao buscar perfil');
      }
      return response.user;
    } catch (error) {
      const message = axios.isAxiosError(error)
        ? error.response?.data?.message ?? 'Erro ao buscar perfil'
        : 'Erro ao buscar perfil';
      throw new Error(message);
    }
  }

  static async updateProfile(data: UpdateProfileRequest): Promise<User> {
    try {
      const response = await fetcher<AuthResponse>({ method: 'put', url: '/api/auth/profile', data });
      if (!response.user) {
        throw new Error('Erro ao atualizar perfil');
      }
      return response.user;
    } catch (error) {
      const message = axios.isAxiosError(error)
        ? error.response?.data?.message ?? 'Erro ao atualizar perfil'
        : 'Erro ao atualizar perfil';
      throw new Error(message);
    }
  }

  static async getMe(): Promise<User> {
    try {
      const response = await fetcher<AuthResponse>({ method: 'get', url: '/api/auth/me' });
      if (!response.user) {
        throw new Error('Erro ao buscar dados do usuário');
      }
      return response.user;
    } catch (error) {
      const message = axios.isAxiosError(error)
        ? error.response?.data?.message ?? 'Erro ao buscar dados do usuário'
        : 'Erro ao buscar dados do usuário';
      throw new Error(message);
    }
  }

  static async logout(): Promise<void> {
    try {
      await fetcher<void>({ method: 'post', url: '/api/auth/logout' });
    } catch {
      // Ignorar erros de logout
    } finally {
      Cookies.remove('auth_token');
      Cookies.remove('user_data');
    }
  }

  static async searchCep(cep: string): Promise<ViaCepResponse> {
    try {
      const response = await fetcher<{ success: boolean; endereco?: ViaCepResponse }>(
        { method: 'get', url: `/api/auth/cep/${cep}` }
      );
      if (response.success && response.endereco) {
        return response.endereco;
      }
      throw new Error('CEP não encontrado');
    } catch (error) {
      const message = axios.isAxiosError(error)
        ? error.response?.data?.message ?? 'Erro ao buscar CEP'
        : 'Erro ao buscar CEP';
      throw new Error(message);
    }
  }

  static getCurrentUser(): User | null {
    try {
      const userData = Cookies.get('user_data');
      return userData ? JSON.parse(userData) : null;
    } catch {
      return null;
    }
  }

  static setCurrentUser(user: User): void {
    try {
      Cookies.set('user_data', JSON.stringify(user), { expires: 7 });
    } catch {
      // Ignorar erros de serialização
    }
  }

  static getToken(): string | null {
    return Cookies.get('auth_token') || null;
  }

  static isAuthenticated(): boolean {
    return !!this.getToken();
  }

  static async changePassword(data: {
    currentPassword: string;
    newPassword: string;
    confirmPassword: string;
  }): Promise<{ success: boolean; message: string }> {
    try {
      return await fetcher<{ success: boolean; message: string }>(
        { method: 'put', url: '/api/auth/change-password', data }
      );
    } catch (error) {
      const message = axios.isAxiosError(error)
        ? error.response?.data?.message ?? 'Erro ao alterar senha'
        : 'Erro ao alterar senha';
      throw new Error(message);
    }
  }
} 